/*
 * PUSA Popup Engine — Padel USA's in-house onsite popup engine (OptiMonk replacement).
 * Phase 1 theme-native MVP. See custom-code-padelusa/features/pusa-popup-engine.md
 * and custom-code-padelusa/docs/custom-popup-engine-plan.md.
 *
 * Design goals (the whole point vs OptiMonk's 47 req/page):
 *   - No external fonts, no third-party popup CDN, no assets on pages where nothing can show.
 *   - Config is INLINED by Liquid (window.PUSA_POPUP_CONFIG) — zero config network request.
 *   - Markup lives in a Liquid <template> (#pusa-popup-tpl); JS clones it and sets text via
 *     textContent only — no innerHTML, no XSS surface.
 *   - Triggers evaluated client-side from first-party signals (localStorage + Liquid context).
 *   - Klaviyo via the PUBLIC company_id client API — same proven pattern as assets/giveaway-entry.js.
 *     No server key, no secrets in the browser.
 *
 * Reads globals injected by snippets/pusa-popup-root.liquid:
 *   window.PUSA_POPUP_CONTEXT  — per-render Liquid page/audience context
 *   window.PUSA_POPUP_CONFIG   — { klaviyo:{companyId}, campaigns:[...] }
 */
(function () {
  'use strict';

  var CFG = window.PUSA_POPUP_CONFIG;
  var CTX = window.PUSA_POPUP_CONTEXT || {};
  if (!CFG || !CFG.campaigns || !CFG.campaigns.length) return;

  // Remote config overlay — owner-edited from the popup dashboard. The worker writes
  // the `pusa.popup_config` shop metafield; Liquid inlines it as PUSA_POPUP_REMOTE, so
  // this stays ZERO extra storefront requests (the engine's founding design goal).
  // Only whitelisted keys merge, per campaign id, defensively: a malformed save
  // degrades to the baked defaults below — it can never brick the popup. Codes/odds
  // are not part of this config (they live server-side, strategy §5b).
  // Baked variants are kept per campaign before the merge: the remote list decides WHICH
  // variants run and their weights, and a remote entry inherits the baked design of the
  // same id — so the dashboard can start a design A/B by adding just
  // {id:'wheel_spin_noir', weight:25}, without re-pasting the whole skin/wheel/copy.
  CFG.campaigns.forEach(function (c) { c.bakedVariants = (c.variants || []).slice(); });
  try {
    var REMOTE = window.PUSA_POPUP_REMOTE;
    if (REMOTE && REMOTE.campaigns) {
      CFG.campaigns.forEach(function (c) {
        var o = REMOTE.campaigns[c.id];
        if (!o || typeof o !== 'object') return;
        if (typeof o.enabled === 'boolean') c.enabled = o.enabled;
        if (Array.isArray(o.triggers) && o.triggers.length) c.triggers = o.triggers;
        if (o.frequency && typeof o.frequency === 'object') { c.frequency = Object.assign({}, c.frequency, o.frequency); }
        if (o.teaser && typeof o.teaser === 'object') { c.teaser = Object.assign({}, c.teaser, o.teaser); }
        // Display rules ("who sees it, where") — editable from the dashboard since 2026-10-09.
        if (o.audience && typeof o.audience === 'object') { c.audience = Object.assign({}, c.audience, o.audience); }
        if (Array.isArray(o.variants) && o.variants.length) {
          c.variants = o.variants.map(function (v) {
            var b = v && bakedVariant(c, v.id);
            return b ? Object.assign({}, b, v) : v;
          });
        }
      });
    }
  } catch (e) {}
  function bakedVariant(c, id) {
    for (var i = 0; i < (c.bakedVariants || []).length; i++) if (c.bakedVariants[i].id === id) return c.bakedVariants[i];
    return null;
  }

  if (CTX.pageType === 'checkout') return;
  // Automated browsers never get the wheel and never count as an impression (a
  // scraper produced ~25k fake impressions/day in Oct 2026; the tools worker also
  // filters by real IP country at ingest). QA overrides below still work in them.
  var BOT = navigator.webdriver === true ||
    /bot\b|crawl|spider|slurp|headless|lighthouse|pagespeed|phantomjs|puppeteer|playwright|selenium/i.test(navigator.userAgent || '');
  if (BOT && !/[?&]pusa_(popup|teaser)_test=1/.test(window.location.search)) return;
  if (window.Shopify && window.Shopify.designMode && !CFG.previewInEditor) return;

  // QA override: add ?pusa_popup_test=1 to any URL to force the popup immediately,
  // bypassing the timer, audience rules, and all suppression. For testing only.
  var TEST = /[?&]pusa_popup_test=1/.test(window.location.search);
  // QA override for the teaser: force-mounts it (and replays the nudge) regardless of
  // audience/frequency state. For testing only.
  var TEASER_TEST = /[?&]pusa_teaser_test=1/.test(window.location.search);
  // QA override: preview a SPECIFIC variant (?pusa_popup_test=1&pusa_popup_variant=<id>).
  // Deliberately NOT persisted to localStorage — a preview must never pin the visitor.
  var VARIANT_FORCE = (window.location.search.match(/[?&]pusa_popup_variant=([\w-]+)/) || [])[1] || '';

  // Must match the exit transition in assets/pusa-popup-engine.css (200ms) + a frame.
  var EXIT_MS = 220;

  var KLAVIYO_REVISION = '2025-07-15';
  var SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions/';
  var PROFILE_URL = 'https://a.klaviyo.com/client/profiles/';
  var EVENT_URL = 'https://a.klaviyo.com/client/events/';

  /* ---------------------------------------------------------------- storage */
  var LS = {
    get: function (k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  };
  var SS = {
    get: function (k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) {} }
  };
  var now = function () { return new Date().getTime(); };
  var DAY = 86400000;

  /* -------------------------------------------------- visitor / session init */
  if (!LS.get('pusa_popup_visitor_id')) {
    LS.set('pusa_popup_visitor_id', 'v_' + Math.random().toString(36).slice(2) + now().toString(36));
    LS.set('pusa_popup_first_seen_at', String(now()));
  }
  LS.set('pusa_popup_last_seen_at', String(now()));
  if (!SS.get('pusa_popup_session_id')) {
    SS.set('pusa_popup_session_id', 's_' + Math.random().toString(36).slice(2));
    LS.set('pusa_popup_session_count', String(parseInt(LS.get('pusa_popup_session_count') || '0', 10) + 1));
  }
  // Pages viewed this session — the "after N pages" display rule (audience.minPageViews).
  SS.set('pusa_popup_pages', String(parseInt(SS.get('pusa_popup_pages') || '0', 10) + 1));
  var sessionPages = parseInt(SS.get('pusa_popup_pages') || '1', 10);
  var sessionId = SS.get('pusa_popup_session_id');
  var sessionCount = parseInt(LS.get('pusa_popup_session_count') || '1', 10);
  var knownLead = LS.get('pusa_popup_known_email') === '1';

  /* ------------------------------------------------------ known subscriber */
  // People already on the list must never be asked to join it again (owner, 2026-10-09:
  // a subscriber clicked a link in one of our emails and still got the wheel). Before
  // this, "known" only meant "spun the wheel in THIS browser" (pusa_popup_known_email),
  // so a subscriber on a new device or browser looked like a stranger. Any of these now
  // marks the browser as a known subscriber, persisted in pusa_popup_known_subscriber:
  //   - a Klaviyo message click: the `_kx` identity token Klaviyo appends to email/SMS
  //     links, or utm_medium=email|sms / utm_source=klaviyo on the landing URL;
  //   - a Klaviyo-identified browser: the `__kla_id` cookie (set by the Klaviyo onsite
  //     embed) carries $exchange_id or $email — the visit came from a Klaviyo message
  //     or a Klaviyo form, on this or an earlier visit;
  //   - a logged-in Shopify customer who accepts marketing (CTX.customerSubscribed);
  //   - a theme newsletter form that was just submitted (?customer_posted=true).
  // Checked at boot AND again right before the popup mounts (render), because
  // klaviyo.js can set the cookie after this script has booted. No network request.
  var KNOWN_SUB_KEY = 'pusa_popup_known_subscriber';
  function klaviyoIdentified() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
      if (!m) return false;
      var raw = decodeURIComponent(m[1]), data;
      try { data = JSON.parse(window.atob(raw)); } catch (e) { data = JSON.parse(raw); }
      return !!(data && (data.$exchange_id || data.$email));
    } catch (e) { return false; }
  }
  function subscriberSignal() {
    var q = window.location.search;
    if (/[?&]_kx=/.test(q)) return 'klaviyo_link';
    if (/[?&]utm_medium=(email|sms)(&|$)/i.test(q) || /[?&]utm_source=[^&]*klaviyo/i.test(q)) return 'email_link';
    if (CTX.customerSubscribed === true) return 'customer_subscribed';
    if (/[?&]customer_posted=true/.test(q)) return 'newsletter_signup';
    if (klaviyoIdentified()) return 'klaviyo_identified';
    return '';
  }
  // Returns the reason string ('' = not known). Persists the first detection so later
  // visits without the link or cookie stay suppressed.
  function knownSubscriber() {
    if (LS.get(KNOWN_SUB_KEY)) return LS.get(KNOWN_SUB_KEY);
    var why = subscriberSignal();
    if (why) LS.set(KNOWN_SUB_KEY, why);
    return why;
  }
  // Winners (spun here) and known subscribers are both "already on the list".
  function onList() { return knownLead || LS.get('pusa_popup_known_email') === '1' || !!knownSubscriber(); }

  /* -------------------------------------------------------------- analytics */
  function emit(name, data) {
    // page_type rides on EVERY event so the dashboard can show where the wheel runs.
    var payload = Object.assign({ event: name, page_type: CTX.pageType }, data || {});
    // Teaser-experiment arm rides on EVERY event once assigned (mountTeaser), so the
    // dashboard can split leads/CVR by arm — including popup submits that happen long
    // after assignment. Absent until a visitor enters the experiment population.
    var teaserArm = LS.get('pusa_popup_teaser_arm');
    if (teaserArm) payload.teaser_arm = teaserArm;
    try { (window.dataLayer = window.dataLayer || []).push(payload); } catch (e) {}
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, data || {});
      }
    } catch (e) {}
    if (CFG.analytics && CFG.analytics.beacon) {
      try { navigator.sendBeacon(CFG.analytics.beacon, JSON.stringify(payload)); } catch (e) {}
    }
  }

  /* ---------------------------------------------------------- eligibility */
  function inList(arr, v) { return !arr || !arr.length || arr.indexOf(v) !== -1; }
  function eligible(c) {
    if (!c.enabled) return false;
    var a = c.audience || {}, f = c.frequency || {};
    if (!audienceOk(c)) return false;
    if (submittedRecently(c)) return false;
    if (frequencySuppressed(c)) return false;
    return true;
  }

  // Same classification as the tools worker's deviceFromUA, so rules and reports agree.
  function deviceType() {
    var ua = navigator.userAgent || '';
    if (/iPad|Tablet/i.test(ua)) return 'tablet';
    if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile';
    return 'desktop';
  }
  // Split out of eligible() so the TEASER can live in the exact gap between them:
  // right person (audience ok, hasn't won) but we won't interrupt again (frequency).
  function audienceOk(c) {
    var a = c.audience || {};
    if (!inList(a.pageTypes, CTX.pageType)) return false;
    if (!inList(a.countries, CTX.countryCode)) return false;
    if (a.visitorTypes && a.visitorTypes.length) {
      var returning = sessionCount >= 2, ok = false;
      if (a.visitorTypes.indexOf('first_time') !== -1 && !returning) ok = true;
      if (a.visitorTypes.indexOf('returning') !== -1 && returning) ok = true;
      if (!ok) return false;
    }
    if (a.devices && a.devices.length && a.devices.indexOf(deviceType()) === -1) return false;
    var path = window.location.pathname;
    if (a.urlInclude && a.urlInclude.length && !a.urlInclude.some(function (u) { return path.indexOf(u) === 0; })) return false;
    if (a.urlExclude && a.urlExclude.some(function (u) { return path.indexOf(u) === 0; })) return false;
    if (a.minPageViews && sessionPages < a.minPageViews) return false;
    if (a.excludeCustomers && CTX.loggedInCustomer === true) return false;
    if (a.excludeKnownSubscribers && onList()) return false;
    return true;
  }
  // Hard stop: they already won — no popup, no teaser (their code is in their inbox).
  function submittedRecently(c) {
    var f = c.frequency || {};
    var subAt = parseInt(LS.get('pusa_popup_submitted_' + c.id) || '0', 10);
    return !!(subAt && f.suppressAfterSubmitDays && now() - subAt < f.suppressAfterSubmitDays * DAY);
  }
  // Soft stop: we chose not to interrupt again (session cap / lifetime cap / spacing /
  // close-window). OptiMonk-parity: persistence converts, but it must decay — a lifetime
  // impression cap + minimum spacing, never an endless nag.
  function frequencySuppressed(c) {
    var f = c.frequency || {};
    if (f.maxPerSession && SS.get('pusa_popup_shown_' + c.id)) return true;
    if (SS.get('pusa_popup_session_shown') && c.respectGlobalSessionCap !== false) return true;
    var shows = parseInt(LS.get('pusa_popup_shows_' + c.id) || '0', 10);
    if (f.maxTotalShows && shows >= f.maxTotalShows) return true;
    var lastShown = parseInt(LS.get('pusa_popup_last_shown_' + c.id) || '0', 10);
    if (f.minHoursBetween && lastShown && now() - lastShown < f.minHoursBetween * 3600000) return true;
    var closedAt = parseInt(LS.get('pusa_popup_closed_' + c.id) || '0', 10);
    if (closedAt && f.suppressAfterCloseDays && now() - closedAt < f.suppressAfterCloseDays * DAY) return true;
    return false;
  }

  /* ----------------------------------------------------- A/B assignment */
  function assignVariant(c) {
    if (!c.variants || !c.variants.length) return null;
    // QA preview of a specific variant — even a disabled one (that's the point:
    // eyeball a design before weighting it into the live A/B). Never stored.
    // Baked variants the remote list doesn't run yet are previewable too.
    var i;
    if (VARIANT_FORCE) {
      for (i = 0; i < c.variants.length; i++) if (c.variants[i].id === VARIANT_FORCE) return c.variants[i];
      if (bakedVariant(c, VARIANT_FORCE)) return bakedVariant(c, VARIANT_FORCE);
    }
    // Dashboard control: a variant can be parked with enabled:false (or weight 0)
    // without deleting its config — it simply leaves the assignment pool.
    var pool = c.variants.filter(function (v) { return v.enabled !== false && (v.weight === undefined || v.weight > 0); });
    if (!pool.length) pool = [c.variants[0]];
    if (pool.length === 1) return pool[0];
    var key = 'pusa_popup_variant_' + c.id, stored = LS.get(key);
    if (stored) { for (i = 0; i < pool.length; i++) if (pool[i].id === stored) return pool[i]; }
    var total = 0; for (i = 0; i < pool.length; i++) total += (pool[i].weight || 1);
    var r = Math.random() * total;
    for (i = 0; i < pool.length; i++) { r -= (pool[i].weight || 1); if (r <= 0) { LS.set(key, pool[i].id); return pool[i]; } }
    LS.set(key, pool[0].id); return pool[0];
  }

  /* -------------------------------------------------- trigger arming */
  // A campaign may declare SEVERAL triggers (c.triggers: [...]) — e.g. exit-intent OR an
  // 8s timer, whichever fires first (OptiMonk's proven combo). The first to fire wins and
  // every armed listener is torn down. Single c.trigger stays supported. fire(type)
  // receives which trigger actually fired, so analytics can compare exit-intent vs timer.
  function arm(c, fire) {
    var list = (c.triggers && c.triggers.length) ? c.triggers : [c.trigger || { type: 'timer', delayMs: 8000 }];
    var done = false, cleanups = [];
    function go(type) { if (done) return; done = true; cleanups.forEach(function (fn) { fn(); }); fire(type); }
    list.forEach(function (t) {
      if (t.type === 'scroll_depth') {
        var pct = t.percent || 40;
        var onScroll = function () { var h = document.documentElement.scrollHeight - window.innerHeight; if (h > 0 && (window.pageYOffset / h) * 100 >= pct) go('scroll_depth'); };
        window.addEventListener('scroll', onScroll, { passive: true });
        cleanups.push(function () { window.removeEventListener('scroll', onScroll); });
      } else if (t.type === 'exit_intent') {
        // Mouse-leaves-viewport-top (desktop). On touch devices there is no reliable,
        // non-gimmicky exit signal — the companion timer trigger covers mobile.
        var onLeave = function (e) { if (e.clientY <= 0) go('exit_intent'); };
        document.addEventListener('mouseout', onLeave);
        cleanups.push(function () { document.removeEventListener('mouseout', onLeave); });
      } else if (t.type === 'idle') {
        var idleMs = t.idleMs || 15000, timer, evs = ['mousemove', 'keydown', 'scroll', 'touchstart'];
        var reset = function () { window.clearTimeout(timer); timer = window.setTimeout(function () { go('idle'); }, idleMs); };
        evs.forEach(function (ev) { window.addEventListener(ev, reset, { passive: true }); }); reset();
        cleanups.push(function () { window.clearTimeout(timer); evs.forEach(function (ev) { window.removeEventListener(ev, reset); }); });
      } else if (t.type === 'cart_added') {
        var onCart = function () { go('cart_added'); };
        document.addEventListener('cart:update', onCart);
        cleanups.push(function () { document.removeEventListener('cart:update', onCart); });
      } else if (t.type === 'manual_event') {
        var name = t.eventName || 'pusa:popup', onManual = function () { go('manual_event'); };
        document.addEventListener(name, onManual);
        cleanups.push(function () { document.removeEventListener(name, onManual); });
      } else {
        var id = window.setTimeout(function () { go('timer'); }, t.delayMs || 8000);
        cleanups.push(function () { window.clearTimeout(id); });
      }
    });
  }

  /* -------------------------------------------------- resolver + boot */
  function boot() {
    if (TEST && CFG.campaigns[0]) { render(CFG.campaigns[0], assignVariant(CFG.campaigns[0]), true); return; }
    if (TEASER_TEST && CFG.campaigns[0]) { mountTeaser(CFG.campaigns[0], assignVariant(CFG.campaigns[0])); return; }
    // Keep the Floky sleeve guided size/quantity purchase paths (Tapia + standard) clear of the
    // wheel and its competing coupon prompt. QA query overrides above still work; other PDPs are unchanged.
    if (/\/products\/floky-no-strain-evolution-sleeve(-x-tapia)?$/.test(window.location.pathname.replace(/\/$/, ''))) return;
    var candidates = [], i, c;
    for (i = 0; i < CFG.campaigns.length; i++) {
      c = CFG.campaigns[i];
      if (eligible(c)) candidates.push(c);
      else emit('pusa_popup_suppressed', { popup_id: c.id, reason: knownSubscriber() ? 'known_subscriber' : undefined });
    }
    if (!candidates.length) {
      // Nothing will auto-show. If someone is the right AUDIENCE but merely
      // frequency-suppressed (closed it before, capped, spaced out) — and has NOT won —
      // give them the manual door back in: the teaser (strategy §6b).
      for (i = 0; i < CFG.campaigns.length; i++) {
        c = CFG.campaigns[i];
        if (c.enabled && audienceOk(c) && !submittedRecently(c) && frequencySuppressed(c)) {
          mountTeaser(c, assignVariant(c));
          break;
        }
      }
      return;
    }
    candidates.sort(function (a, b) { return (b.priority || 0) - (a.priority || 0); });
    var chosen = candidates[0], variant = assignVariant(chosen);
    ACTIVE = { campaign: chosen, variant: variant };
    emit('pusa_popup_eligible', { popup_id: chosen.id, variant_id: variant && variant.id });
    if (variant) emit('pusa_popup_experiment_assigned', { popup_id: chosen.id, variant_id: variant.id, variant_type: variant.type });
    arm(chosen, function (firedBy) { render(chosen, variant, false, firedBy); });
  }

  /* -------------------------------------------------- teaser (strategy §6b) */
  // The persistent, manual door back into the wheel. It exists ONLY in the gap between
  // "right audience" and "we won't interrupt again": after a dismissal, or when the
  // frequency caps/spacing block the auto-popup. Winners never see it. It never fights
  // a pending auto-popup. Left edge, mid-height — the one region of this theme with no
  // CTAs (bottom = sticky ATC/cart, right = chat, top = header), on both breakpoints.
  var ACTIVE = null;
  var TEASER = { el: null, timers: [] };

  function mountTeaser(campaign, variant) {
    if (TEASER.el) { teaserComeback(); return; }
    // Hard gate, independent of campaign audience config: winners and known subscribers
    // never get teased (they're already on the list; the door is for the unconverted).
    if (onList() && !TEASER_TEST) return;
    // They ×-ed the tab away: stay hidden for the rest of the session. The door
    // reopens next session — a teaser close is "not now", not "never".
    if (SS.get('pusa_popup_teaser_closed') === '1' && !TEASER_TEST) return;
    var t = campaign.teaser || {};
    if (t.enabled === false) return;
    // 50/50 teaser experiment (owner ask 2026-07-17): does the manual door back in
    // actually add leads? The arm is assigned ONCE, lazily, at the first moment the
    // teaser WOULD mount — the tightest honest population (suppressed-but-unconverted
    // visitors; everyone else never reaches this line) — and sticks to the visitor
    // (localStorage). 'off' = no teaser, but every later event still carries
    // teaser_arm (see emit), so the dashboard compares leads per assigned visitor.
    // Kill switch: teaser.experiment (remote-config-editable). QA mounts skip it.
    if (t.experiment && !TEASER_TEST) {
      var arm = LS.get('pusa_popup_teaser_arm');
      if (!arm) {
        arm = Math.random() < 0.5 ? 'on' : 'off';
        LS.set('pusa_popup_teaser_arm', arm);
        emit('pusa_popup_teaser_arm_assigned', { popup_id: campaign.id,
          page_type: CTX.pageType, session_id: sessionId });
      }
      if (arm === 'off') return;
    }
    var tpl = document.getElementById('pusa-teaser-tpl');
    if (!tpl || !tpl.content) return;
    var frag = tpl.content.cloneNode(true);
    var btn = frag.querySelector('[data-pusa-teaser]');
    if (!btn) return;
    if (t.label) { var lab = btn.querySelector('[data-pusa-teaser-label]'); if (lab) lab.textContent = t.label; }
    // The tab wears the same skin as the popup the visitor will open from it.
    if (variant && variant.skin) btn.setAttribute('data-pusa-skin', variant.skin);
    document.body.appendChild(frag);
    TEASER.el = btn;

    // Enter softly, then nudge: two short wiggle sets (~6s and ~14s in), each 2 cycles,
    // then STILL — and only on the first page of the session (owner: "nudge just a few
    // times and then stop"). prefers-reduced-motion gets a plain fade, no travel.
    window.setTimeout(function () { btn.classList.add('pusa-teaser--in'); }, 900);
    if (!SS.get('pusa_popup_teaser_nudged') || TEASER_TEST) {
      SS.set('pusa_popup_teaser_nudged', '1');
      [6000, 14000].forEach(function (ms) {
        TEASER.timers.push(window.setTimeout(function () {
          btn.classList.add('pusa-teaser--nudge');
          window.setTimeout(function () { btn.classList.remove('pusa-teaser--nudge'); }, 2000);
        }, ms));
      });
    }

    var shownMeta = { popup_id: campaign.id, variant_id: variant && variant.id,
      page_type: CTX.pageType, session_id: sessionId };
    // QA teaser sessions must be excludable from metrics like QA popups are: the
    // ingest flags trigger_type 'forced' as is_test. Real teaser events carry no
    // trigger_type (shown/clicked/closed) or 'teaser' (the popup open in render).
    if (TEASER_TEST) shownMeta.trigger_type = 'forced';
    var emitShown = function () { emit('pusa_popup_teaser_shown', shownMeta); };
    if (document.hidden) {
      document.addEventListener('visibilitychange', function onVis() {
        if (document.hidden) return;
        document.removeEventListener('visibilitychange', onVis);
        emitShown();
      });
    } else { emitShown(); }

    var openBtn = btn.querySelector('[data-pusa-teaser-open]') || btn;
    openBtn.addEventListener('click', function () {
      emit('pusa_popup_teaser_clicked', shownMeta);
      btn.classList.remove('pusa-teaser--in');
      // Manual open: bypasses eligibility AND the frequency counters (force=true skips
      // them) — the shopper asked for it. trigger_type 'teaser' keeps analytics honest:
      // the open counts as an impression, attributed to the teaser in the trigger split.
      // QA opens report 'forced' instead, so they're excluded like every QA event.
      render(campaign, variant, true, TEASER_TEST ? 'forced' : 'teaser');
    });

    // The ×: slide out and stay gone for the session (owner ask: the tab must be
    // dismissable). Never burns popup frequency counters — closing the teaser is not
    // closing the popup.
    var closeBtn = btn.querySelector('[data-pusa-teaser-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (ev) {
        ev.stopPropagation();
        emit('pusa_popup_teaser_closed', shownMeta);
        SS.set('pusa_popup_teaser_closed', '1');
        TEASER.timers.forEach(function (id) { window.clearTimeout(id); });
        TEASER.timers = [];
        btn.classList.remove('pusa-teaser--in', 'pusa-teaser--nudge');
        window.setTimeout(function () {
          if (btn.parentNode) btn.parentNode.removeChild(btn);
          if (TEASER.el === btn) TEASER.el = null;
        }, 520);
      });
    }
  }

  // After the popup closes (auto-shown then dismissed, or teaser-opened then dismissed),
  // the door stays open — unless they just won, or they ×-ed the tab away this session.
  function teaserComeback() {
    if (onList()) return;
    if (SS.get('pusa_popup_teaser_closed') === '1') return;
    if (TEASER.el) {
      window.setTimeout(function () { TEASER.el.classList.add('pusa-teaser--in'); }, 650);
    } else if (ACTIVE) {
      mountTeaser(ACTIVE.campaign, ACTIVE.variant);
    }
  }

  /* ------------------------------------------------------ Klaviyo client */
  // Identify THIS browser to klaviyo.js (sets the __kla_id cookie) so the visitor's later
  // Viewed Product / Added to Cart events attach to their profile. The client API calls in
  // klaviyoSubmit create the profile but never identify the browser; OptiMonk's Klaviyo
  // integration did, and identified on-site events halved when the wheel replaced it in
  // July 2026 (Klaviyo audit 2026-10-20, G1). klaviyo.js loads async from the app embed, so
  // wait for it; if it never shows within 20s, leave the call on the legacy _learnq queue,
  // which klaviyo.js drains whenever it does load. Ad-blocked: harmless no-op.
  function klaviyoIdentify(email) {
    var tries = 0;
    (function attempt() {
      var k = window.klaviyo;
      if (k && typeof k.identify === 'function') {
        try { var p = k.identify({ email: email }); if (p && p.catch) p.catch(function () {}); } catch (e) {}
        return;
      }
      if (++tries <= 40) { window.setTimeout(attempt, 500); return; }
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', { $email: email }]);
    })();
  }

  function klaviyoSubmit(campaign, email, props) {
    var companyId = (CFG.klaviyo && CFG.klaviyo.companyId) || '';
    if (!companyId || !email) return;
    klaviyoIdentify(email);
    var opt = function (body) { return { method: 'POST', mode: 'cors', keepalive: true,
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION }, body: JSON.stringify(body) }; };
    // Event — records the "Spun Wheel" metric with reward + reward_code (independent).
    fetch(EVENT_URL + '?company_id=' + encodeURIComponent(companyId),
      opt({ data: { type: 'event', attributes: {
        metric: { data: { type: 'metric', attributes: { name: campaign.klaviyoMetric || 'Spun Wheel' } } },
        properties: props || {}, profile: { data: { type: 'profile', attributes: { email: email } } } } } })).catch(function () {});
    // Stamp profile properties (reward, reward_code, …) FIRST, THEN join the list — so the
    // list-triggered flow reliably sees reward_code when it fires (avoids the race that would
    // otherwise send an email with a blank coupon).
    var profileReq = fetch(PROFILE_URL + '?company_id=' + encodeURIComponent(companyId),
      opt({ data: { type: 'profile', attributes: { email: email, properties: props || {} } } }));
    if (campaign.klaviyoListId) {
      profileReq.then(function () {
        return fetch(SUBSCRIBE_URL + '?company_id=' + encodeURIComponent(companyId),
          opt({ data: { type: 'subscription', attributes: { profile: { data: { type: 'profile', attributes: {
            email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } },
            relationships: { list: { data: { type: 'list', id: campaign.klaviyoListId } } } } }));
      }).catch(function () {});
    } else {
      profileReq.catch(function () {});
    }
  }

  /* ------------------------------------------------------------- render */
  function clearRoot(root) { while (root.firstChild) root.removeChild(root.firstChild); }

  function render(campaign, variant, force, firedBy) {
    if (!force && SS.get('pusa_popup_session_shown')) return;
    // Last-moment recheck: klaviyo.js may have identified the visitor (cookie) after boot.
    if (!force && (campaign.audience || {}).excludeKnownSubscribers && onList()) {
      emit('pusa_popup_suppressed', { popup_id: campaign.id, reason: 'known_subscriber' });
      return;
    }
    // An intentional quiz takes priority. Do not count an unseen wheel impression behind it.
    if (document.querySelector('product-finder-dialog dialog[open]')) return;
    var root = document.getElementById('pusa-popup-root');
    var tpl = document.getElementById('pusa-popup-tpl');
    if (!root || !tpl || !tpl.content) return;

    // Never mount into a hidden tab. setTimeout keeps running while a tab is
    // backgrounded, so a visitor who opens the store in a background tab would have the
    // popup mount unseen — emitting a pusa_popup_viewed impression nobody saw, which
    // inflates impressions and understates the lead-capture rate (leads / impressions).
    // Wait for the tab instead: the trigger delay then measures time actually spent
    // LOOKING at the page, which is what it was always meant to mean.
    if (document.hidden) {
      document.addEventListener('visibilitychange', function onVisible() {
        if (document.hidden) return;
        document.removeEventListener('visibilitychange', onVisible);
        render(campaign, variant, force, firedBy);
      });
      return;
    }

    SS.set('pusa_popup_session_shown', '1');
    SS.set('pusa_popup_shown_' + campaign.id, '1');
    // Lifetime impression counter + spacing stamp (drives frequency.maxTotalShows /
    // minHoursBetween). Skipped in forced/QA mode so test spins never burn the real cap.
    if (!force) {
      LS.set('pusa_popup_shows_' + campaign.id, String(parseInt(LS.get('pusa_popup_shows_' + campaign.id) || '0', 10) + 1));
      LS.set('pusa_popup_last_shown_' + campaign.id, String(now()));
    }

    clearRoot(root);
    root.classList.remove('pusa-pop-in');
    root.classList.remove('pusa-pop-out');
    // Design A/B: a variant may carry a `skin` (court / mono / noir — styled in
    // pusa-popup-engine.css). No skin = the original Ivory control, untouched.
    var skin = variant && variant.skin;
    if (skin) root.setAttribute('data-pusa-skin', skin); else root.removeAttribute('data-pusa-skin');
    root.appendChild(tpl.content.cloneNode(true));
    document.body.classList.add('pusa-pop-open');
    // Force a style/layout flush so the browser COMMITS the closed state (veil
    // transparent, card low + slightly small) before we flip to open — otherwise it
    // coalesces both states into one paint and the popup snaps in with no motion.
    // Deliberately not requestAnimationFrame: rAF is paused in background tabs, and a
    // starved flip would leave an invisible full-screen overlay sitting over the page.
    void root.offsetWidth;
    root.classList.add('pusa-pop-in');

    var wheel = (variant && variant.wheel) || campaign.wheel || {};
    // `segments` are the decorative wheel faces only. The winning prize + its code come
    // from the server (see requestSpin) — no codes or odds exist client-side anymore.
    var segments = wheel.segments || [];

    var q = function (sel) { return root.querySelector(sel); };
    // An empty string hides the element (e.g. the premium skins drop the eyebrow:
    // DESIGN.md §8 — no decorative eyebrows).
    var setText = function (sel, txt) {
      var el = q(sel); if (!el || txt == null) return;
      el.textContent = txt; el.hidden = txt === '';
    };
    // A variant may override any copy field (and `wheel` above) — that's what makes a
    // variant a self-contained design/copy A/B the dashboard can manage. Falls back
    // to the campaign per field (only when the variant leaves the field undefined).
    var vv = variant || {};
    var pick = function (k) { return vv[k] !== undefined ? vv[k] : campaign[k]; };
    setText('[data-pusa-eyebrow]', pick('eyebrow'));
    // Social proof (variant.proof): star row + one TRUE line, e.g. the homepage's own
    // "10,000+ US padel players" and the Judge.me 4.9 aggregate. Never invent numbers.
    var proofEl = q('[data-pusa-proof]');
    if (proofEl) {
      proofEl.hidden = !vv.proof;
      if (vv.proof) setText('[data-pusa-proof-text]', vv.proof);
    }
    setText('[data-pusa-head]', pick('headline'));
    setText('[data-pusa-sub]', pick('subtext'));
    setText('[data-pusa-cta]', pick('cta'));
    setText('[data-pusa-fine]', pick('fine'));
    // "Conditions apply" links to the campaign's rules page (campaign.rulesUrl): the prize
    // conditions (code minimums, in-stock racket only) live there, not in the popup.
    var rulesUrl = pick('rulesUrl'), fineCopy = q('[data-pusa-fine]');
    if (rulesUrl && fineCopy && /Conditions apply/.test(fineCopy.textContent)) {
      var fineParts = fineCopy.textContent.split('Conditions apply'), rulesLink = document.createElement('a');
      rulesLink.href = rulesUrl; rulesLink.target = '_blank'; rulesLink.rel = 'noopener';
      rulesLink.className = 'pusa-pop__rules'; rulesLink.textContent = 'Conditions apply';
      fineCopy.textContent = fineParts[0];
      fineCopy.appendChild(rulesLink);
      fineCopy.appendChild(document.createTextNode(fineParts.slice(1).join('Conditions apply')));
    }

    var meta = { popup_id: campaign.id, variant_id: variant && variant.id, variant_type: variant && variant.type,
      trigger_type: firedBy || (force ? 'forced' : (campaign.trigger || {}).type), page_type: CTX.pageType, country_code: CTX.countryCode,
      visitor_type: sessionCount >= 2 ? 'returning' : 'first_time', session_id: sessionId };
    emit('pusa_popup_viewed', meta);

    // Game format: the wheel (default) or scratch cards (variant.type 'scratch_card').
    // Both use the same server-side draw, the same lead counting and the same reward
    // screen — only the reveal differs, so formats compare fairly in the A/B.
    var isScratch = vv.type === 'scratch_card';
    // Two-step (variant.prestep): one easy yes/no before the email form — the "yes"
    // is the micro-commitment (owner, 2026-10-10: ask "Want to win a padel racket?",
    // not a survey question). Options are strings (continue to the form) or objects
    // {label, close: true} (a quiet "No thanks" that closes like ×). DOM nodes only.
    var preAnswer = '';
    if (vv.prestep && vv.prestep.options && vv.prestep.options.length) {
      var capEl = q('[data-pusa-capture]'), pre = q('[data-pusa-prestep]');
      if (capEl && pre) {
        capEl.hidden = true; pre.hidden = false;
        var pq = pre.querySelector('[data-pusa-prestep-q]'), ps = pre.querySelector('[data-pusa-prestep-sub]'), po = pre.querySelector('[data-pusa-prestep-opts]');
        if (pq) pq.textContent = vv.prestep.question || '';
        if (ps) { ps.textContent = vv.prestep.subtext || ''; ps.hidden = !vv.prestep.subtext; }
        vv.prestep.options.forEach(function (opt) {
          var label = typeof opt === 'string' ? opt : opt.label, closes = typeof opt === 'object' && opt.close;
          var b = document.createElement('button');
          b.type = 'button'; b.className = closes ? 'pusa-pop__opt pusa-pop__opt--quiet' : 'pusa-pop__opt'; b.textContent = label;
          b.addEventListener('click', function () {
            preAnswer = label;
            emit('pusa_popup_prestep_answered', Object.assign({ answer: label }, meta));
            if (closes) { dismiss(); return; }
            pre.hidden = true; capEl.hidden = false;
            var em = q('[data-pusa-email]'); if (em) em.focus({ preventScroll: true });
          });
          po.appendChild(b);
        });
      }
    }
    root.setAttribute('data-pusa-kind', isScratch ? 'scratch' : 'wheel');
    if (isScratch) var scratch = scratchGame(root, wheel);
    else drawWheel(q('[data-pusa-wheel]'), segments, wheel);

    // Play the exit transition, THEN tear the popup down. Guarded so Escape and a
    // click can't both run it.
    var dismissed = false;
    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      LS.set('pusa_popup_closed_' + campaign.id, String(now()));
      // 'closed' must mean ABANDONED (left without spinning) — that's the close-rate
      // the dashboard reports. A close after the reward screen ("Shop now →", Escape)
      // is a completed journey, not an abandonment: own event name, out of the rate.
      emit(spun ? 'pusa_popup_result_closed' : 'pusa_popup_closed', meta);
      document.removeEventListener('keydown', onKey);
      root.classList.remove('pusa-pop-in');
      root.classList.add('pusa-pop-out');
      window.setTimeout(function () {
        document.body.classList.remove('pusa-pop-open');
        root.classList.remove('pusa-pop-out');
        root.removeAttribute('data-pusa-skin');
        clearRoot(root);
        // The comeback door: a dismissal is not a "no forever" — surface the teaser
        // (unless they won: knownLead gates inside).
        if (!ACTIVE) ACTIVE = { campaign: campaign, variant: variant };
        teaserComeback();
      }, EXIT_MS);
    }
    function onKey(e) { if (e.key === 'Escape' || e.key === 'Esc') dismiss(); }

    document.addEventListener('keydown', onKey);
    Array.prototype.forEach.call(root.querySelectorAll('[data-pusa-close]'), function (el) {
      el.addEventListener('click', dismiss);
    });

    var emailEl = q('[data-pusa-email]'), spinBtn = q('[data-pusa-spin]'), wheelEl = q('[data-pusa-wheel]');
    var spun = false, rotation = 0;
    function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

    var fineEl = q('[data-pusa-fine]');
    spinBtn.addEventListener('click', function () {
      if (spun) return;
      if (!validEmail(emailEl.value)) { emailEl.classList.add('pusa-pop__field--err'); emailEl.focus(); return; }
      emailEl.classList.remove('pusa-pop__field--err');
      var email = emailEl.value.trim();
      spun = true; spinBtn.disabled = true; emailEl.disabled = true;
      var ctaLabel = spinBtn.textContent; spinBtn.textContent = isScratch ? 'Unlocking…' : 'Spinning…';
      if (fineEl) fineEl.classList.remove('pusa-pop__fine--err');
      emit('pusa_popup_interaction_started', meta);

      // The DRAW is server-side. Ask the worker which prize/code this email won (it
      // records a per-email ledger, so a repeat email replays the first result), THEN
      // land the wheel on that segment. We never draw here — there are no codes to draw.
      requestSpin(campaign, variant, email).then(function (res) {
        var prize = res.prize;
        if (!isScratch) {
          var seg = segIndexFor(segments, prize.label);
          var jitter = (Math.random() - 0.5) * (360 / segments.length - 10);
          rotation += 360 * 6 - seg * (360 / segments.length) + jitter;
          wheelEl.style.transform = 'rotate(' + rotation + 'deg)';
        }

        LS.set('pusa_popup_known_email', '1');
        LS.set('pusa_popup_submitted_' + campaign.id, String(now()));
        var props = { popup_id: campaign.id, popup_type: variant ? variant.type : 'wheel_spin',
          variant_id: variant && variant.id, reward: prize.label, reward_code: prize.code || '',
          lead_source: 'pusa_popup', url: location.href, referrer: document.referrer };

        // Only a FIRST spin is a new lead (counted). A replay re-shows the prize they
        // already won — counting it would inflate the lead-capture CVR the OptiMonk A/B is
        // judged on. But it DOES re-fire the (idempotent) Klaviyo submit as a self-heal:
        // if the first spin's Klaviyo calls were blocked (ad-blockers eat a.klaviyo.com),
        // a later spin from a clean browser repairs the profile + list membership. The
        // list is single opt-in and re-joining while already a member is a no-op, so this
        // never double-mails. (Real case 2026-07-16: a winner's first-spin subscribe was
        // silently blocked; the replay left them permanently off the list.)
        if (res.replay) {
          klaviyoSubmit(campaign, email, props);
          emit('pusa_popup_replay', Object.assign({ reward: prize.label }, meta));
        } else {
          klaviyoSubmit(campaign, email, props);
          emit('pusa_popup_submitted', Object.assign({ reward: prize.label }, meta));
        }
        tagCart(campaign, props); // stamp the cart either way — they may buy this session

        var reveal = function () {
          showResult(root, prize, res.replay, variant);
          emit('pusa_popup_reward_revealed', Object.assign({ reward: prize.label, replay: !!res.replay }, meta));
          emit('pusa_popup_interaction_completed', Object.assign({ reward: prize.label }, meta));
        };
        if (isScratch) {
          // The lead is already captured (above); now they scratch a card to see the prize.
          // Hold the card's height so hiding the form doesn't jump the cards under a finger.
          var popEl = q('.pusa-pop'); if (popEl) popEl.style.minHeight = popEl.offsetHeight + 'px';
          setText('[data-pusa-head]', vv.scratchHeadline || 'Scratch a card');
          setText('[data-pusa-sub]', vv.scratchSubtext || 'Your prize is under whichever one you choose.');
          spinBtn.hidden = true; emailEl.hidden = true;
          if (fineEl) fineEl.hidden = true;
          scratch.arm(prize, reveal);
        } else {
          window.setTimeout(reveal, 5300);
        }
      }).catch(function () {
        // FAIL CLOSED. There is no client-side prize table to fall back to (that is the
        // whole point), so on any error we re-enable the form and let them retry rather
        // than fabricate a prize.
        spun = false; spinBtn.disabled = false; emailEl.disabled = false;
        spinBtn.textContent = ctaLabel;
        if (fineEl) { fineEl.textContent = 'Hmm, that didn’t go through. Please try again.'; fineEl.classList.add('pusa-pop__fine--err'); }
        emit('pusa_popup_spin_error', meta);
      });
    });
  }

  /* Ask the worker to draw (or replay) this email's prize. Resolves to
     { ok, replay, prize:{label, code, grand} }; rejects on any transport/shape error
     so the caller can fail closed. */
  function requestSpin(campaign, variant, email) {
    var ep = CFG.spin && CFG.spin.endpoint;
    if (!ep) return Promise.reject(new Error('no spin endpoint'));
    // Abort a hung request so the wheel can't stall on "Spinning…" forever; the caller
    // fails closed on the resulting rejection.
    var ctrl = window.AbortController ? new AbortController() : null;
    var timer = ctrl ? window.setTimeout(function () { ctrl.abort(); }, 12000) : null;
    var clear = function () { if (timer) { window.clearTimeout(timer); timer = null; } };
    return fetch(ep, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, campaign: campaign.id, variant: variant && variant.id }),
      signal: ctrl ? ctrl.signal : undefined
    }).then(function (r) {
      clear();
      if (!r.ok) throw new Error('spin http ' + r.status);
      return r.json();
    }, function (err) { clear(); throw err; }).then(function (data) {
      if (!data || !data.ok || !data.prize || !data.prize.label) throw new Error('bad spin response');
      return data;
    });
  }

  /* Session-based revenue attribution: stamp a hidden cart attribute so ANY order
     placed this session/cart carries the wheel spin — attributable even if the
     shopper never uses (or manually types) the discount code. Private "_"-prefixed
     attributes are hidden from the customer but persist onto the order's note_attributes. */
  function tagCart(campaign, props) {
    try {
      fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: {
          _wheel_spin: 'yes',
          _wheel_campaign: campaign.id,
          _wheel_variant: props.variant_id || '',
          _wheel_prize: props.reward || '',
          _wheel_code: props.reward_code || '',
          _wheel_ts: String(now())
        } })
      }).catch(function () {});
    } catch (e) {}
  }

  /* --------------------------------------------------- wheel internals */
  // (The prize draw used to live here as pickPrize(); it now runs server-side in the
  //  worker so the codes and odds never reach the browser. See requestSpin.)
  function segIndexFor(segments, label) {
    var idx = [], i; for (i = 0; i < segments.length; i++) if (segments[i] === label) idx.push(i);
    return idx.length ? idx[Math.floor(Math.random() * idx.length)] : 0;
  }
  // Painter options (all optional; absent = the original Ivory look, pixel for pixel):
  //   sliceFills / textFills  slice + label colours, cycled together (give one entry
  //                           per segment to paint every slice explicitly)
  //   grandFill / grandText   the FREE RACKET slices
  //   stroke                  slice divider colour
  //   rim {fill, dot, line, width}  a game-show rim: a light at every slice edge + centre
  //   labelCase 'sentence'    paints "$15 off" / "Free racket" (DESIGN.md §8 sentence
  //                           case); prize matching still uses the raw segment labels.
  /* Scratch cards (2026-10-10, owner: "people are curious and want to know what's
     behind them"). Three foil cards; they unlock only after the server draw, then the
     shopper scratches one (pointer drag) or taps / presses Enter on one (auto reveal).
     Honest by design: the prize is the same whichever card is chosen, so the scratched
     card shows it and the other two simply fade — they never show "other prizes" that
     would imply the choice mattered. The prize text is written into the DOM only AFTER
     the draw, so nothing is readable in the page before a lead is captured. */
  function scratchGame(root, wheel) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-pusa-card]'));
    var hint = root.querySelector('[data-pusa-scratch-hint]');
    var cs = window.getComputedStyle(root);
    var foilA = (cs.getPropertyValue('--sk-foil-a') || '').trim() || '#dcdcdc';
    var foilB = (cs.getPropertyValue('--sk-foil-b') || '').trim() || '#a9a9a9';
    var foilInk = (cs.getPropertyValue('--sk-foil-ink') || '').trim() || 'rgba(10,10,10,.55)';
    var done = false, armed = null;

    function paint(cv) {
      var dpr = Math.min(window.devicePixelRatio || 1, 2), w = cv.clientWidth || 120, hgt = cv.clientHeight || 160;
      cv.width = Math.round(w * dpr); cv.height = Math.round(hgt * dpr);
      var c = cv.getContext('2d');
      c.globalCompositeOperation = 'source-over';
      var g = c.createLinearGradient(0, 0, cv.width, cv.height);
      g.addColorStop(0, foilA); g.addColorStop(0.5, foilB); g.addColorStop(1, foilA);
      c.fillStyle = g; c.fillRect(0, 0, cv.width, cv.height);
      c.globalAlpha = 0.12; c.strokeStyle = '#fff'; c.lineWidth = 2 * dpr;
      for (var x = -cv.height; x < cv.width; x += 10 * dpr) { c.beginPath(); c.moveTo(x, cv.height); c.lineTo(x + cv.height, 0); c.stroke(); }
      c.globalAlpha = 1; c.fillStyle = foilInk; c.textAlign = 'center';
      c.font = '500 ' + Math.round(30 * dpr) + 'px ' + (wheel.font || "'Satoshi', sans-serif");
      c.fillText('?', cv.width / 2, cv.height / 2 + 4 * dpr);
      c.font = '500 ' + Math.round(11 * dpr) + 'px ' + (wheel.font || "'Satoshi', sans-serif");
      c.fillText('Scratch', cv.width / 2, cv.height / 2 + 26 * dpr);
    }
    function cleared(cv) {
      var c = cv.getContext('2d'), d = c.getImageData(0, 0, cv.width, cv.height).data, clear = 0, n = 0;
      for (var i = 3; i < d.length; i += 4 * 24) { n++; if (d[i] < 40) clear++; }
      return n ? clear / n : 0;
    }
    function finish(card) {
      if (done) return; done = true;
      card.classList.add('pusa-scratch__card--won');
      cards.forEach(function (o) {
        o.disabled = true;
        if (o !== card) o.classList.add('pusa-scratch__card--out');
      });
      if (hint) hint.textContent = '';
      window.setTimeout(armed, 1300);
    }

    cards.forEach(function (card, idx) {
      var cv = card.querySelector('canvas'), drawing = false, moved = 0, last = null, strokes = 0;
      paint(cv);
      var pos = function (e) {
        var r = cv.getBoundingClientRect(), k = cv.width / r.width;
        return { x: (e.clientX - r.left) * k, y: (e.clientY - r.top) * k };
      };
      cv.addEventListener('pointerdown', function (e) {
        if (!armed || done) return;
        drawing = true; moved = 0; last = pos(e);
        try { cv.setPointerCapture(e.pointerId); } catch (err) {}
        e.preventDefault();
      });
      cv.addEventListener('pointermove', function (e) {
        if (!drawing || done) return;
        var p = pos(e), c = cv.getContext('2d');
        moved += Math.abs(p.x - last.x) + Math.abs(p.y - last.y);
        c.globalCompositeOperation = 'destination-out';
        c.lineWidth = 30 * (cv.width / cv.clientWidth); c.lineCap = 'round'; c.lineJoin = 'round';
        c.beginPath(); c.moveTo(last.x, last.y); c.lineTo(p.x, p.y); c.stroke();
        last = p;
        if (++strokes % 8 === 0 && cleared(cv) > 0.45) { drawing = false; finish(card); }
      });
      var up = function () {
        if (!drawing) return; drawing = false;
        if (moved < 12 * (cv.width / cv.clientWidth)) finish(card); // a tap reveals too
      };
      cv.addEventListener('pointerup', up);
      cv.addEventListener('pointercancel', function () { drawing = false; });
      // Keyboard / assistive tech: the card is a button; Enter or Space reveals it.
      card.addEventListener('click', function (e) { if (armed && !done && e.detail === 0) finish(card); });
      card.setAttribute('aria-label', 'Card ' + (idx + 1) + ' of ' + cards.length);
    });

    return {
      arm: function (prize, onReveal) {
        var label = wheelLabel(prize.label, wheel);
        cards.forEach(function (card) {
          var slot = card.querySelector('[data-pusa-card-prize]');
          if (slot) slot.textContent = label;
          card.disabled = false;
          card.classList.add('pusa-scratch__card--live');
        });
        if (hint) hint.textContent = 'Scratch or tap a card';
        armed = onReveal;
        if (cards[0]) cards[0].focus({ preventScroll: true });
      }
    };
  }

  function sentenceCase(s) { s = String(s).toLowerCase(); return s.charAt(0).toUpperCase() + s.slice(1); }
  function wheelLabel(label, wheel) { return wheel.labelCase === 'sentence' ? sentenceCase(label) : label; }
  function drawWheel(canvas, segments, wheel) {
    if (!canvas || !canvas.getContext) return;
    var ctx = canvas.getContext('2d'), R = canvas.width / 2, N = segments.length, SEG = 360 / N;
    var fills = wheel.sliceFills || ['#F4E3C8', '#FFFFFF'], texts = wheel.textFills || ['#2a2a2a'], i, a;
    var rim = wheel.rim || null, rimW = rim ? (rim.width || 30) : 0, r = R - 6 - rimW;
    var rad = function (deg) { return deg * Math.PI / 180; };
    if (rim) { ctx.beginPath(); ctx.arc(R, R, R - 2, 0, Math.PI * 2); ctx.fillStyle = rim.fill || '#0a0a0a'; ctx.fill(); }
    for (i = 0; i < N; i++) {
      var a0 = rad(i * SEG - 90 - SEG / 2), a1 = rad((i + 1) * SEG - 90 - SEG / 2);
      ctx.beginPath(); ctx.moveTo(R, R); ctx.arc(R, R, r, a0, a1); ctx.closePath();
      var label = segments[i], grand = /racket/i.test(label), lose = /almost|sorry/i.test(label);
      ctx.fillStyle = grand ? (wheel.grandFill || '#C9A24B') : lose ? '#efe7da' : fills[i % fills.length];
      ctx.fill(); ctx.strokeStyle = wheel.stroke || 'rgba(0,0,0,.10)'; ctx.lineWidth = 2; ctx.stroke();
      ctx.save(); ctx.translate(R, R); ctx.rotate(rad(i * SEG - 90));
      ctx.textAlign = 'right'; ctx.fillStyle = grand ? (wheel.grandText || '#3a2a00') : texts[i % texts.length];
      var text = wheelLabel(label, wheel);
      ctx.font = (grand ? '700 ' : '500 ') + (text.length > 7 ? 26 : 29) + 'px ' + (wheel.font || "'Satoshi', sans-serif");
      ctx.fillText(text, r - 28, 10); ctx.restore();
    }
    if (rim && rim.line) { ctx.beginPath(); ctx.arc(R, R, r, 0, Math.PI * 2); ctx.strokeStyle = rim.line; ctx.lineWidth = 3; ctx.stroke(); }
    if (rim && rim.dot) {
      for (i = 0; i < N * 2; i++) {
        a = rad(i * SEG / 2 - 90 - SEG / 2);
        ctx.beginPath(); ctx.arc(R + Math.cos(a) * (R - 4 - rimW / 2), R + Math.sin(a) * (R - 4 - rimW / 2), rimW * 0.16, 0, Math.PI * 2);
        ctx.fillStyle = rim.dot; ctx.fill();
      }
    }
  }
  // Result copy. The skinned variants follow DESIGN.md §8 (sentence case, no exclamation
  // marks); the control keeps its original words so the A/B baseline holds.
  var RESULT_COPY = {
    control: {
      grand: 'Big jackpot!', grandReplay: 'Your padel racket',
      won: function (l) { return 'You won ' + l + '!'; }, wonReplay: function (l) { return 'You already won ' + l; },
      grandSub: "We just emailed you the instructions to collect your padel racket. Check your inbox now — and your spam/junk folder if you don't see it.",
      grandReplaySub: 'You already spun and won a racket — check the email we sent with your claim instructions (and your spam/junk folder).',
      sub: 'Use this code at checkout — we emailed it to you too.',
      replaySub: 'This is the code from your spin — one per player. Use it at checkout.'
    },
    premium: {
      grand: 'You won a padel racket', grandReplay: 'Your padel racket',
      won: function (l) { return 'You won ' + l; }, wonReplay: function (l) { return 'You already won ' + l; },
      grandSub: "We've emailed you how to claim it and pick your racket (conditions apply). Check your spam folder if it isn't there.",
      grandReplaySub: 'You already won a racket. Your claim instructions are in the email we sent you.',
      sub: 'Use this code at checkout. We emailed it to you too.',
      replaySub: 'This is the code you already won. One per player.'
    }
  };
  function showResult(root, prize, replay, variant) {
    var cap = root.querySelector('[data-pusa-capture]'), res = root.querySelector('[data-pusa-result]');
    if (cap) cap.style.display = 'none';
    var proofRow = root.querySelector('[data-pusa-proof]'); if (proofRow) proofRow.hidden = true;
    if (!res) return;
    res.style.display = 'flex';
    var grand = /racket/i.test(prize.label);
    var premium = !!(variant && variant.skin), C = premium ? RESULT_COPY.premium : RESULT_COPY.control;
    var label = premium ? prize.label.toLowerCase() : prize.label;
    // On a replay we hand back the SAME prize the email won the first time, so there's
    // nothing to gain by re-spinning. The copy says so plainly instead of pretending
    // it's a fresh win. Grand: no code either way (racket is fulfilled by email reply).
    root.querySelector('[data-pusa-rtitle]').textContent = grand
      ? (replay ? C.grandReplay : C.grand)
      : (replay ? C.wonReplay(label) : C.won(label));
    root.querySelector('[data-pusa-rsub]').textContent = grand
      ? (replay ? C.grandReplaySub : C.grandSub)
      : (replay ? C.replaySub : C.sub);
    var codeWrap = root.querySelector('[data-pusa-codewrap]');
    if (!grand && prize.code) {
      codeWrap.style.display = 'flex';
      root.querySelector('[data-pusa-code]').textContent = prize.code;
    } else if (codeWrap) { codeWrap.style.display = 'none'; }
    var copyBtn = root.querySelector('[data-pusa-copy]');
    if (copyBtn) copyBtn.addEventListener('click', function () {
      try { navigator.clipboard.writeText(prize.code); } catch (e) {}
      copyBtn.textContent = 'Copied';
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
